export function compact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}m`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(n);
}

export function money(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString('en-US')}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Parse yyyy-mm-dd as a local calendar date (no timezone shifts). */
export function parseIsoDate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number) as [number, number, number];
  return new Date(y, m - 1, d);
}

export function toIsoDate(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * A calendar day (yyyy-mm-dd) as the day it names; an instant (with a time part) as the day it
 * falls on in the host's zone, so an evening here is not tomorrow's date in UTC.
 */
export function shortDate(iso: string): string {
  const d = iso.includes('T') ? new Date(iso) : parseIsoDate(iso.slice(0, 10));
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

/** "Mar 2024": when a post was posted, at the grain that matters for an archive. */
export function monthYear(iso: string): string {
  const d = iso.includes('T') ? new Date(iso) : parseIsoDate(iso.slice(0, 10));
  return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

export function longDate(iso: string): string {
  const d = new Date(iso);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

export function dayLabel(d: Date): string {
  return `${DAYS[(d.getDay() + 6) % 7]} ${d.getDate()}`;
}

export function monthName(d: Date): string {
  return d.toLocaleString('en-US', { month: 'long' });
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

/** Monday of the week containing d. */
export function startOfWeek(d: Date): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const offset = (x.getDay() + 6) % 7;
  return addDays(x, -offset);
}

export function weekDays(from: Date): Date[] {
  return Array.from({ length: 7 }, (_, i) => addDays(from, i));
}

export function greeting(name: string, now = new Date()): string {
  const h = now.getHours();
  const part = h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
  const first = name.trim().split(/\s+/)[0] ?? '';
  return first ? `${part}, ${first}` : part;
}

export function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

export { DAYS, MONTHS };
